import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Crown, Eye, EyeOff, Clock, MessageCircle, Star, Ban, UserMinus } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import { isHost as isHostRole, sessionSettings } from '../utils/roles';
import { Avatar } from './Avatar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const Participants = ({ session, onMessageUser }) => {
  const { user } = useAuth();
  const { hostAction } = useSocket();
  const participants = Object.values(session.participants || {});
  const viewerIsHost = isHostRole(session, user?.uid);
  const settings = sessionSettings(session);
  const [kicking, setKicking] = useState(null);

  const formatTime = (timestamp) => {
    const now = new Date();
    const joined = new Date(timestamp);
    const diff = now - joined;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just joined';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleToggleCoHost = (participant) => {
    const promote = participant.role !== 'cohost';
    hostAction('setRole', participant.uid, null, { role: promote ? 'cohost' : 'guest' });
    toast.success(
      promote
        ? `${participant.displayName} is now a co-host`
        : `${participant.displayName} is no longer a co-host`,
      { icon: promote ? '🎧' : undefined }
    );
  };

  const handleToggleBlock = (participant) => {
    // Blocking silences someone — worth a confirm; unblocking is benign
    if (!participant.isBlocked &&
        !window.confirm(`Block ${participant.displayName}? They won't be able to suggest songs, vote, or chat.`)) {
      return;
    }
    hostAction(participant.isBlocked ? 'unblockUser' : 'blockUser', participant.uid);
    toast.success(
      participant.isBlocked
        ? `${participant.displayName} unblocked`
        : `${participant.displayName} blocked`
    );
  };

  const handleKick = async (participant) => {
    if (!window.confirm(`Remove ${participant.displayName} from the session?`)) return;
    setKicking(participant.uid);
    try {
      await api.delete(`/api/sessions/${session.id}/participants/${participant.uid}`);
      toast.success(`${participant.displayName} removed from session`);
    } catch (error) {
      console.error('Kick error:', error);
      toast.error(error.response?.data?.error || 'Failed to remove participant');
    } finally {
      setKicking(null);
    }
  };

  if (participants.length === 0) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No participants</h3>
          <p className="text-gray-400">
            Be the first to join this session!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-xl font-semibold mb-6 flex items-center">
        <Users className="w-5 h-5 mr-2" />
        Participants ({participants.length})
      </h2>

      {renderSections()}
    </div>
  );

  function renderSections() {
    const hosts = participants.filter((p) => p.isHost);
    const vibing = participants.filter((p) => !p.isHost && p.isVisible !== false);
    const layingLow = participants.filter((p) => !p.isHost && p.isVisible === false);

    const section = (title, list) =>
      list.length > 0 && (
        <div key={title} className="mb-5 last:mb-0">
          <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-gray-400 mb-2">
            {title}
          </h3>
          <div className="grid gap-3">{list.map(renderParticipant)}</div>
        </div>
      );

    return (
      <>
        {section('Host', hosts)}
        {section(`Vibing Now (${vibing.length})`, vibing)}
        {section(`Laying Low (${layingLow.length})`, layingLow)}
      </>
    );
  }

  function renderParticipant(participant, index) {
          const isSelf = participant.uid === user?.uid;
          const isCoHost = participant.role === 'cohost';
          const canModerate = viewerIsHost && !isSelf && !participant.isHost;

          return (
            <motion.div
              key={participant.uid}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-gray-700 rounded-lg p-4 border ${
                isSelf ? 'border-purple-500' : 'border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-3 min-w-0">
                  {/* Avatar with presence dot */}
                  <span className="relative shrink-0">
                    <Avatar
                      uid={participant.uid}
                      name={participant.displayName}
                      photoURL={participant.photoURL}
                      size="lg"
                    />
                    {participant.isVisible && !participant.isBlocked && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full ring-2 ring-gray-800" />
                    )}
                  </span>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <h3 className="font-semibold text-white min-w-0 truncate">
                        {participant.displayName}
                      </h3>
                      {participant.isHost && (
                        <Crown className="w-4 h-4 text-yellow-400 shrink-0" title="Host" />
                      )}
                      {isCoHost && (
                        <span className="flex items-center gap-1 text-xs bg-indigo-600/30 text-indigo-300 px-2 py-0.5 rounded shrink-0">
                          <Star className="w-3 h-3" />
                          Co-host
                        </span>
                      )}
                      {isSelf && (
                        <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded shrink-0">
                          You
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-400">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTime(participant.joinedAt)}
                      </span>
                      {participant.suggestionCount > 0 && (
                        <span>
                          {participant.suggestionCount} suggestions
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Indicators & Actions */}
                <div className="flex items-center space-x-2 shrink-0 flex-wrap justify-end">
                  {/* DM Button - show for visible, non-blocked, non-self users */}
                  {!isSelf &&
                    settings.dmsEnabled &&
                    participant.isVisible &&
                    !participant.isBlocked &&
                    onMessageUser && (
                      <button
                        onClick={() =>
                          onMessageUser({
                            uid: participant.uid,
                            displayName: participant.displayName
                          })
                        }
                        className="p-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
                        title={`Message ${participant.displayName}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    )}
                  {participant.isVisible ? (
                    <Eye className="w-4 h-4 text-green-400" title="Visible" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" title="Invisible" />
                  )}
                  {participant.isBlocked && (
                    <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">
                      Blocked
                    </span>
                  )}
                </div>
              </div>

              {/* Host moderation row */}
              {canModerate && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-600">
                  <button
                    onClick={() => handleToggleCoHost(participant)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors ${
                      isCoHost
                        ? 'bg-indigo-600/30 text-indigo-300 hover:bg-indigo-600/40'
                        : 'bg-gray-600 text-gray-300 hover:bg-indigo-600/30 hover:text-indigo-300'
                    }`}
                  >
                    <Star className="w-3.5 h-3.5" />
                    {isCoHost ? 'Remove co-host' : 'Make co-host'}
                  </button>
                  <button
                    onClick={() => handleToggleBlock(participant)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors ${
                      participant.isBlocked
                        ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                        : 'bg-gray-600 text-gray-300 hover:bg-red-600/30 hover:text-red-300'
                    }`}
                  >
                    <Ban className="w-3.5 h-3.5" />
                    {participant.isBlocked ? 'Unblock' : 'Block'}
                  </button>
                  <button
                    onClick={() => handleKick(participant)}
                    disabled={kicking === participant.uid}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs bg-gray-600 text-gray-300 hover:bg-red-600/30 hover:text-red-300 transition-colors disabled:opacity-50"
                    title="Remove from session"
                  >
                    <UserMinus className="w-3.5 h-3.5" />
                    {kicking === participant.uid ? '...' : 'Kick'}
                  </button>
                </div>
              )}
            </motion.div>
          );
  }
};

export default Participants;
